import {
  FC,
  Fragment,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Field } from 'react-final-form';
import {
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
  PlaidLinkOnExit,
} from 'react-plaid-link';
import styled from 'styled-components';
import {
  Brand,
  RegistrationFieldPermissionsField,
} from '@stax-core/types-omni';
import { coreapi, onboardingapi } from '../../api';
import { useWindowSize, useToaster } from '../../hooks';
import {
  Button,
  Input,
  Group,
  FieldError,
  FormHeader,
  ToggleableField,
  ToggleableFieldProps,
  ResponsiveMargin,
  Select,
  Checkbox,
  ResponsivePadding,
  FormImage,
} from '../../components';
import { FieldPermissionFlag } from '../../components/FieldPermissionFlag';
import {
  PlaidAccountNumbers,
  PlaidLink,
  PlaidSkeletonLoader,
  PlaidText,
} from '../../components/Plaid';
import { formatRoutingNumber } from '../../util/format.util';
import { handlePlaidLinkError } from '../../util/plaid.util';
import { accountPurpose, accountType } from '../../util/registration.util';
import {
  BrandStore,
  AuthStore,
  RegistrationStore,
  updateRegistration,
} from '../../context';
import {
  FinancialFieldsProps,
  PlaidAccountBase,
  PlaidAccountType,
} from './FinancialFields.types';
import { RepresentativeFields } from './RepresentativeFields';
import {
  hasPrimaryManualBankAccount,
  hasSecondaryManualBankAccount,
} from './FinancialFields.util';
import signupExampleCheckSvg from '../../assets/img/signup_exampleCheck.svg';

/**
 * Financial account fields have varying labels based upon the
 * "Should show trust and operating" boolean BRAND value.
 *
 * Additionally, when this brand value is true, the field
 * "secondary_bank_account_purpose" must only be set to the value "trust"
 *
 * @see https://fattmerchant.atlassian.net/browse/KRK-1373
 */
export const getBrandedFinancialText = (key: string, brand: Brand | null) => {
  const isTrustOperating = brand && brand.shouldShowTrustAndOperating;

  const textMap = {
    default: {
      primaryHeading: 'Financial Institution',
      secondaryHeading: 'Secondary Account',
      secondaryOptInLabel:
        'I have an additional bank account to be used for fees, trust, discount, credits, or chargebacks',
      secondaryOptInTooltip:
        'Adding a secondary bank account is optional. This account will not be used for depositing funds, and can only be used for the purposes indicated, e.g. fees can be drafted out of this account.',
    },
    trustAndOperating: {
      primaryHeading: 'Operating Account',
      secondaryHeading: 'Trust Account',
      secondaryOptInLabel: 'I need to add a trust account',
      secondaryOptInTooltip:
        'Adding a trust account is optional. This account is used to receive funds. Any fees generated from the account will be taken out of the associated operating account. If you need to add additional operating and/or trust accounts, this can be done after the initial approval.',
    },
  };

  return textMap[isTrustOperating ? 'trustAndOperating' : 'default'][key];
};

const StyledError = styled.div`
  width: 100%;
  height: auto;
  margin-bottom: 20px;

  span {
    font-size: 15px;
    height: 20px;
    width: 100%;
    position: relative !important;
  }
`;

export const FinancialFields: FC<FinancialFieldsProps> = props => {
  const {
    mode,
    form: { mutators },
    values: { addAccountManually, hasSecondaryAccount },
    setDisableFormSubmission,
  } = props;
  const { isMobile } = useWindowSize();
  const {
    state: { brand },
  } = useContext(BrandStore);
  const allowPlaid = brand?.allowPlaid;
  const {
    state: { auth },
  } = useContext(AuthStore);
  const merchantId = auth?.merchant?.id;
  const userId = auth?.user?.id;
  const authToken = auth?.token ?? null;
  const authTokenRef = useRef<string>();
  authTokenRef.current = authToken!; // keep the reference updated

  const { dispatch } = useContext(RegistrationStore);

  const {
    state: { registration: storedRegistration },
  } = useContext(RegistrationStore);

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [plaidAccountNumbers, setPlaidAccountNumbers] = useState<
    PlaidAccountBase[]
  >([]);
  const [fetchingAccounts, setFetchingAccounts] = useState(true);
  const { toaster, toast } = useToaster();

  /** Whether the brand allows 2nd accounts */
  const shouldShowSecondaryBankAccountFields =
    brand?.shouldShowSecondaryBankAccountFields;
  const hasPrimaryManualAccount =
    hasPrimaryManualBankAccount(props.values) && plaidAccountNumbers.length < 1;
  /** whether there is already a primary account linked (either Plaid or manual fields) */
  const hasPrimaryAccount =
    plaidAccountNumbers.length > 0 || hasPrimaryManualAccount;

  useEffect(() => {
    if (!fetchingAccounts) {
      // If the primary account is removed, hide the secondary account fields and disable the checkbox so that
      // a second account cannot be added until a primary account is added
      if (hasSecondaryAccount && !hasPrimaryAccount) {
        mutators.setFieldValue('hasSecondaryAccount', false);
      }
      // If has a primary manual account or has a secondary account (manual or Plaid)
      // then the 'addAccountManually' checkbox is checked
      if (
        !addAccountManually &&
        (hasPrimaryManualAccount || hasSecondaryAccount)
      ) {
        mutators.setFieldValue('addAccountManually', true);
      }
    }
  }, [
    mutators,
    addAccountManually,
    fetchingAccounts,
    hasPrimaryAccount,
    hasPrimaryManualAccount,
    hasSecondaryAccount,
  ]);

  const fetchAndSetAccountNumbers = useCallback(async () => {
    if (!allowPlaid) {
      return;
    }

    const accountsResponse: { accounts: PlaidAccountType[] } =
      await onboardingapi.get(authTokenRef.current!, '/banking/accounts');

    if (accountsResponse?.accounts) {
      const maskedAccounts =
        accountsResponse?.accounts?.map(account => ({
          mask: account.mask,
          name: account.name,
          plaid_token_id: account.plaid_token_id,
          account_type: account.account_type,
        })) ?? [];

      setPlaidAccountNumbers(maskedAccounts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowPlaid,
    setPlaidAccountNumbers,
    // see comment for the verifyBank() useEffect() below and how it uses merchantId and userId as deps instead of authToken
    merchantId,
  ]);

  useEffect(() => {
    if (!allowPlaid) {
      return;
    }
    const verifyBank = async () => {
      try {
        setFetchingAccounts(true);
        const { token } = await onboardingapi.get(
          authTokenRef.current!,
          '/banking/verify',
        );
        localStorage.setItem(`linkToken.${merchantId}`, token);
        setLinkToken(token);
        await fetchAndSetAccountNumbers();
        setFetchingAccounts(false);
      } catch (error) {
        setFetchingAccounts(false);
        toaster(
          toast.error(
            error?.message ??
              'There was a problem retrieving your account information',
          ),
        );
      }
    };
    verifyBank();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allowPlaid,
    fetchAndSetAccountNumbers,
    toaster,
    toast,
    // these deps are included instead of 'authToken' and 'authTokenRef' is used inside this effect because authToken is asynchronously
    // refreshed in App.tsx when the page loads, a race condition can occur and a new auth token can be dispatch()'ed to AuthStore after
    // this effect has rendered that triggers this effect to re-run because the authToken is different but the data in the authToken that
    // the '/banking/verify' route uses (merchantId and userId) may not have changed, so use merchantId and userId as deps instead of
    // authToken to prevent the unnecessary re-run of this effect and service calls.
    merchantId,
    userId,
  ]);

  // The usePlaidLink hook manages Plaid Link creation
  const onSuccess = useCallback<PlaidLinkOnSuccess>(
    async (public_token: string, metadata: PlaidLinkOnSuccessMetadata) => {
      setLinkToken(null);
      setFetchingAccounts(true);
      try {
        await onboardingapi.post(authToken!, '/banking/token/exchange', {
          public_token,
          metadata,
        });
        const { token } = await onboardingapi.get(
          authToken!,
          '/banking/verify',
        );
        localStorage.setItem(`linkToken.${merchantId}`, token);
        setLinkToken(token);
        await fetchAndSetAccountNumbers();
        const r = await coreapi.get(authToken!, '/team/registration');
        dispatch(updateRegistration(r));
      } catch (error: any) {
        toaster(toast.error('There was a problem linking your account'));
      }
      setFetchingAccounts(false);
    },
    [
      authToken,
      merchantId,
      fetchAndSetAccountNumbers,
      dispatch,
      toaster,
      toast,
    ],
  );

  const onExit = useMemo<PlaidLinkOnExit>(
    () => handlePlaidLinkError(authToken!, (_, type) => {}),
    [authToken],
  );

  useEffect(() => {
    if (allowPlaid) {
      setDisableFormSubmission?.(!!fetchingAccounts);
    }
  }, [fetchingAccounts, setDisableFormSubmission, allowPlaid]);

  const defaultFieldProps: Partial<ToggleableFieldProps<any, HTMLElement>> = {
    toggleable: props.mode === 'review',
    defaultMode: props.mode === 'review' ? 'display' : 'edit',
  };

  return (
    <>
      <FieldPermissionFlag
        fields={
          [
            'title',
            'user_dob',
            'user_ssn',
            'first_name',
            'last_name',
            'meta.ownership_percentage',
            'email',
            'phone_number',
            'owner_address_1',
            // "owner_address_2" is not necessary
            'owner_address_city',
            'owner_address_state',
            'owner_address_country',
            'owner_address_zip',
          ] as RegistrationFieldPermissionsField[]
        }
        renderIf="atLeastOneRead"
      >
        <FormHeader
          data-testid={mode === 'review' ? 'review-financial' : null}
          tag="h3"
          bordered
          style={{ marginTop: 0, paddingTop: 0 }}
        >
          Business Owner Information
        </FormHeader>
        <ResponsivePadding>
          <Group direction="column" space="1rem/0px">
            <RepresentativeFields {...props} isMobile={isMobile} />
            {props.principalSignerFooter}
          </Group>
        </ResponsivePadding>
      </FieldPermissionFlag>
      <FieldPermissionFlag
        fields={[
          'meta.representatives.0.title',
          'meta.representatives.0.date_of_birth',
          'meta.representatives.0.first_name',
          'meta.representatives.0.last_name',
          'meta.representatives.0.type',
          'meta.representatives.0.ssn',
          'meta.representatives.0.ownership_percentage',
          'meta.representatives.0.email',
          'meta.representatives.0.phone',
          'meta.representatives.0.address_1',
          // NOTE: 'meta.representatives.0.address_2' field is not necessary
          'meta.representatives.0.address_city',

          'meta.representatives.0.address_country',
          'meta.representatives.0.address_zip',
        ]}
      >
        {({ atLeastOneRead, atLeastOneWrite }) => {
          return (
            <>
              {/* only show the representatives if at least one of the necessary fields for one rep are readable */}
              {atLeastOneRead &&
                Array.isArray(props.values?.meta?.representatives) &&
                props.values.meta.representatives.map((_, i) => (
                  <Fragment key={i}>
                    <div>
                      <FormHeader
                        tag="h3"
                        data-testid="review-representatives"
                        bordered
                      >
                        {isMobile
                          ? 'Addl. Representative'
                          : 'Additional Representative'}{' '}
                        {i + 1}
                      </FormHeader>
                      {atLeastOneWrite ? (
                        <Button
                          variant="outline"
                          motif="destructive"
                          id={`remove-additional-rep-${i}-button`}
                          data-testid={`remove-additional-rep-${i}-button`}
                          size="tiny"
                          icon="fas fa-times"
                          style={{
                            float: 'right',
                            marginTop: '-50px',
                            marginRight: '40%',
                          }}
                          onClick={() =>
                            props.form.mutators.removeAdditionalRepresentative(
                              i,
                            )
                          }
                        >
                          <span>Remove</span>
                        </Button>
                      ) : null}
                    </div>
                    <ResponsivePadding>
                      <Group direction="column" space="1rem/0px">
                        <RepresentativeFields
                          {...props}
                          index={i}
                          isMobile={isMobile}
                        />
                      </Group>
                    </ResponsivePadding>
                  </Fragment>
                ))}
              {props.mode === 'edit' &&
                storedRegistration?.company_type !== 'S' && (
                  <ResponsiveMargin desktop="0px" mobile="0px 8px">
                    {/* only show the add rep button if at least one of the necessary fields for one rep are writeable and not a sole proprietor */}
                    {atLeastOneWrite ? (
                      <Button
                        motif="primary"
                        size="small"
                        variant="outline"
                        id="add-additional-rep-button"
                        data-testid="add-additional-rep-button"
                        onClick={() =>
                          props.form.mutators.addAdditionalRepresentative()
                        }
                      >
                        + Additional Representative
                      </Button>
                    ) : null}

                    <small>
                      Effective May 11, 2018, the Federal FinCen (Financial
                      Crimes Enforcement Network) requires all financial
                      institutions to collect the Control Owner and Beneficial
                      Owners on entities that are not otherwise exempt. Please
                      provide information on any additional representatives if
                      any representatives (Beneficial Owner) directly or
                      indirectly own 25% or more of the entity.
                    </small>
                  </ResponsiveMargin>
                )}
            </>
          );
        }}
      </FieldPermissionFlag>
      <FieldPermissionFlag
        fields={
          [
            'bank_account_owner_name',
            'bank_routing_number',
            'bank_account_number',
          ] as RegistrationFieldPermissionsField[]
        }
        // render the financial section if at least one necessary field is readable
        renderIf="atLeastOneRead"
      >
        <FormHeader tag="h3" bordered>
          {getBrandedFinancialText('primaryHeading', brand)}
        </FormHeader>

        {allowPlaid && (
          <Group data-testid="plaid-group" direction="row" space="1rem/0px">
            <Group direction={'column'}>
              {plaidAccountNumbers.length > 0 && (
                <PlaidAccountNumbers
                  accounts={plaidAccountNumbers}
                  updateAccountNumbers={fetchAndSetAccountNumbers}
                  setUpdatingAccounts={setFetchingAccounts}
                  updatingAccounts={fetchingAccounts}
                  setLinkToken={setLinkToken}
                  hasManualSecondaryAccount={
                    plaidAccountNumbers.length < 2 &&
                    hasSecondaryManualBankAccount(props.values)
                  }
                />
              )}
              {!fetchingAccounts &&
                plaidAccountNumbers.length < 1 &&
                !hasPrimaryAccount &&
                !hasSecondaryAccount && (
                  <ResponsiveMargin
                    desktop="6px 0px 3px"
                    mobile="15px 8px"
                    data-testid="plaid-no-accounts-linked-header"
                  >
                    <PlaidText as={'h2'}>No Accounts Linked</PlaidText>
                  </ResponsiveMargin>
                )}
              {fetchingAccounts && (
                <Group direction={'row'}>
                  <ResponsiveMargin
                    desktop="10px 0px 20px"
                    mobile="15px 8px"
                    data-testid="plaid-fetch-accounts"
                  >
                    <PlaidSkeletonLoader />
                  </ResponsiveMargin>
                </Group>
              )}
              {/*
              // Show Plaid Link button if account fetch is not in progress and
              // 1. We have not linked any accounts
              // 2. We have linked a primary account, and not a secondary account, and brand should show secondary account field
              */}
              {!fetchingAccounts &&
                (!hasPrimaryAccount ||
                  (plaidAccountNumbers.length === 1 &&
                    shouldShowSecondaryBankAccountFields &&
                    !hasSecondaryAccount)) && (
                  <ResponsiveMargin
                    desktop="0px"
                    mobile="0px 8px"
                    data-testid="plaid-button"
                  >
                    <PlaidLink
                      linkToken={linkToken!}
                      onSuccess={onSuccess}
                      onExit={onExit}
                      text={`${
                        plaidAccountNumbers.length > 0 ? ' + ' : ''
                      }Link Bank Account`}
                    />
                  </ResponsiveMargin>
                )}
              {/*
              // Show Add Account Manually Checkbox if
              // 1. Less than 2 accounts are linked
              */}
              {!addAccountManually && (
                <Group direction="column">
                  <StyledError>
                    <FieldError name="bank_account_number" />
                  </StyledError>
                  <StyledError>
                    <FieldError name="bank_routing_number" />
                  </StyledError>
                </Group>
              )}
              {!fetchingAccounts && plaidAccountNumbers.length < 2 && (
                <Group direction="row" style={{ marginTop: '20px' }}>
                  <ResponsiveMargin mobile="1rem 0.5rem" desktop="0">
                    <Field name="addAccountManually" type="checkbox">
                      {props => (
                        <Checkbox
                          {...props.input}
                          id="addAccountManually"
                          data-testid="plaid-add-account-manually"
                          label={`${
                            plaidAccountNumbers.length === 1
                              ? 'Add Secondary '
                              : 'Or Add '
                          }Account Manually`}
                        />
                      )}
                    </Field>
                  </ResponsiveMargin>
                </Group>
              )}
            </Group>
          </Group>
        )}

        {/*
        // Show Primary Account manual fields if
        // 1. Plaid is not allowed
        // 2. addAccountManually checkbox is checked and no Plaid accounts have been linked
        */}
        {(!allowPlaid ||
          (addAccountManually && plaidAccountNumbers.length < 1)) && (
          <Group
            data-testid="financial-institution-primary-group"
            direction={isMobile ? 'column-reverse' : 'row'}
          >
            <div style={{ flex: '50%', marginBottom: 0 }}>
              <Group direction="column" space="1rem/0px">
                <Group direction="column">
                  <ToggleableField
                    name="bank_account_owner_name"
                    label="Name on Bank Account"
                    {...defaultFieldProps}
                  >
                    {props => <Input {...props.input} />}
                  </ToggleableField>
                  <FieldError name="bank_account_owner_name" />
                </Group>

                <Group direction="column">
                  <ToggleableField
                    name="bank_routing_number"
                    label="Routing Number"
                    parse={formatRoutingNumber}
                    format={formatRoutingNumber}
                    {...defaultFieldProps}
                  >
                    {props => <Input {...props.input} maxLength={9} />}
                  </ToggleableField>
                  <FieldError name="bank_routing_number" />
                </Group>

                <Group direction="column">
                  <ToggleableField
                    name="bank_account_number"
                    label="Account Number"
                    {...defaultFieldProps}
                  >
                    {props => (
                      <Input
                        {...props.input}
                        type="number"
                        onWheel={event => event.currentTarget.blur()}
                      />
                    )}
                  </ToggleableField>
                  <FieldError name="bank_account_number" />
                </Group>

                {props.mode === 'edit' && (
                  <Group direction="column">
                    <ToggleableField
                      name="bankAccountNumberConfirm"
                      label="Confirm Account Number"
                      {...defaultFieldProps}
                    >
                      {props => (
                        <Input
                          {...props.input}
                          type="number"
                          onWheel={event => event.currentTarget.blur()}
                        />
                      )}
                    </ToggleableField>
                    <FieldError name="bankAccountNumberConfirm" />
                  </Group>
                )}
                <Group direction="column" space="1rem/0px">
                  {shouldShowSecondaryBankAccountFields && (
                    <FieldPermissionFlag
                      fields={
                        [
                          'secondary_bank_account_owner_name',
                          'secondary_bank_routing_number',
                          'secondary_bank_account_number',
                          'secondary_bank_account_type',
                          'secondary_bank_account_purpose',
                        ] as RegistrationFieldPermissionsField[]
                      }
                      // only show this checkbox if at least one of the relevant fields are writeable
                      renderIf="atLeastOneWrite"
                    >
                      <Group direction="row">
                        <ResponsiveMargin mobile="1rem 0.5rem" desktop="0">
                          <Field name="hasSecondaryAccount" type="checkbox">
                            {props => (
                              <Checkbox
                                {...props.input}
                                disabled={
                                  props.input.disabled || !hasPrimaryAccount
                                }
                                id="hasSecondaryAccount"
                                data-testid="financial-fields-has-secondary-account"
                                label={getBrandedFinancialText(
                                  'secondaryOptInLabel',
                                  brand,
                                )}
                                tooltip={getBrandedFinancialText(
                                  'secondaryOptInTooltip',
                                  brand,
                                )}
                              />
                            )}
                          </Field>
                        </ResponsiveMargin>
                      </Group>
                    </FieldPermissionFlag>
                  )}
                </Group>
              </Group>
            </div>
            {props.mode === 'edit' && (
              <FormImage
                src={signupExampleCheckSvg}
                style={{ flex: '50%', marginBottom: 'auto' }}
              />
            )}
          </Group>
        )}

        {/*
        // Show Secondary Account if
        // 1. none or one Plaid account has been linked and hasSecondaryAccount,
        //      this works for allowPlaid true or false because there will be no plaidAccountNumbers if Plaid is no allowed
        // 2. a primary Plaid account is already linked, and then addAccountManually is checked
        */}
        {((plaidAccountNumbers.length < 2 && hasSecondaryAccount) ||
          (plaidAccountNumbers.length === 1 && addAccountManually)) && (
          <>
            <FormHeader tag="h3" bordered>
              {getBrandedFinancialText('secondaryHeading', brand)}
            </FormHeader>
            <Group
              data-testid="financial-institution-secondary-group"
              direction={isMobile ? 'column-reverse' : 'row'}
            >
              <div style={{ width: '50%' }}>
                <Group direction="column" space="1rem/0px">
                  <Group direction="column">
                    <ToggleableField
                      name="secondary_bank_account_owner_name"
                      data-testid="test_secondary_bank_account_owner_name"
                      label="Account Owner Name"
                      {...defaultFieldProps}
                    >
                      {props => <Input {...props.input} />}
                    </ToggleableField>
                    <FieldError name="secondary_bank_account_owner_name" />
                  </Group>

                  <Group direction="column">
                    <ToggleableField
                      name="secondary_bank_routing_number"
                      data-testid="test_secondary_bank_routing_number"
                      label="Routing Number"
                      parse={formatRoutingNumber}
                      format={formatRoutingNumber}
                      {...defaultFieldProps}
                    >
                      {props => <Input {...props.input} maxLength={9} />}
                    </ToggleableField>
                    <FieldError name="secondary_bank_routing_number" />
                  </Group>
                  <Group direction="column">
                    <ToggleableField
                      name="secondary_bank_account_number"
                      data-testid="test_secondary_bank_account_number"
                      label="Account Number"
                      {...defaultFieldProps}
                    >
                      {props => (
                        <Input
                          {...props.input}
                          type="number"
                          onWheel={event => event.currentTarget.blur()}
                        />
                      )}
                    </ToggleableField>
                    <FieldError name="secondary_bank_account_number" />
                  </Group>

                  {props.mode === 'edit' && (
                    <Group direction="column">
                      <ToggleableField
                        name="secondaryBankAccountNumberConfirm"
                        label="Confirm Account Number"
                        {...defaultFieldProps}
                      >
                        {props => (
                          <Input
                            {...props.input}
                            type="number"
                            onWheel={event => event.currentTarget.blur()}
                          />
                        )}
                      </ToggleableField>
                      <FieldError name="secondaryBankAccountNumberConfirm" />
                    </Group>
                  )}

                  <Group direction="column">
                    <ToggleableField
                      name="secondary_bank_account_type"
                      data-testid="test_secondary_bank_account_type"
                      label="Account Type"
                      {...defaultFieldProps}
                      type="select"
                      options={accountType}
                      optionLabelKey="name"
                      optionValueKey="value"
                    >
                      {props => (
                        <Select {...props.input}>
                          <option value="" disabled />
                          {accountType.map((e, index) => (
                            <option key={index} value={e.value}>
                              {e.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    </ToggleableField>
                    <FieldError name="secondary_bank_account_type" />
                  </Group>

                  <Group direction="column">
                    {brand?.shouldShowTrustAndOperating ? (
                      <Field
                        type="hidden"
                        name="secondary_bank_account_purpose"
                        defaultValue="trust"
                        value="trust"
                        initialValue="trust"
                      >
                        {props => (
                          <input
                            {...props.input}
                            value="trust"
                            data-testid="secondary-bank-account-purpose-trust-operating"
                          />
                        )}
                      </Field>
                    ) : (
                      <ToggleableField
                        name="secondary_bank_account_purpose"
                        label="Account is used for?"
                        {...defaultFieldProps}
                        type="select"
                        options={accountPurpose}
                        optionLabelKey="name"
                        optionValueKey="value"
                      >
                        {props => (
                          <Select {...props.input}>
                            <option value="" disabled />
                            {accountPurpose.map((e, index) => (
                              <option key={index} value={e.value}>
                                {e.name}
                              </option>
                            ))}
                          </Select>
                        )}
                      </ToggleableField>
                    )}

                    <FieldError name="secondary_bank_account_purpose" />
                  </Group>
                </Group>
              </div>
            </Group>
          </>
        )}
      </FieldPermissionFlag>
      {props.children}
    </>
  );
};

export default FinancialFields;
